import { useEffect, useState, useCallback } from 'react';

const BUILD_INFO_URL = '/build-info.json';
const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

export const CHUNK_LOAD_ERROR_EVENT = 'merchant-center:chunk-load-error';

export interface BuildInfo {
  version: string;
  timestamp: number;
  time: string;
}

function getCurrentBuildTimestamp(): number | undefined {
  return typeof window !== 'undefined' ? window.__BUILD_TIMESTAMP__ : undefined;
}

/**
 * Fetches build-info.json with cache busting to detect when a new deployment is available.
 * Call refresh() to reload the app and get the latest code.
 */
export function useVersionCheck() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const currentBuild = getCurrentBuildTimestamp();

  const checkVersion = useCallback(async () => {
    if (import.meta.env.DEV || currentBuild == null) return;
    try {
      const res = await fetch(`${BUILD_INFO_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { Pragma: 'no-cache' },
      });
      if (!res.ok) return;
      const data: BuildInfo = await res.json();
      if (typeof data.timestamp === 'number' && data.timestamp > currentBuild) {
        setNewVersionAvailable(true);
      }
    } catch {
      // Ignore network errors; user may be offline
    }
  }, [currentBuild]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV || currentBuild == null) return;

    const runCheck = () => {
      void checkVersion();
    };

    const initialCheckTimer = window.setTimeout(runCheck, 0);
    const interval = setInterval(runCheck, POLL_INTERVAL_MS);

    const onChunkLoadError = () => setNewVersionAvailable(true);
    window.addEventListener(CHUNK_LOAD_ERROR_EVENT, onChunkLoadError);

    return () => {
      clearTimeout(initialCheckTimer);
      clearInterval(interval);
      window.removeEventListener(CHUNK_LOAD_ERROR_EVENT, onChunkLoadError);
    };
  }, [checkVersion, currentBuild]);

  return { newVersionAvailable, refresh };
}
